import React, { useState, useEffect, useRef } from "react";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import Divider from "@mui/material/Divider";
import { Typography, Menu, Badge, Tooltip, Box } from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CheckIcon from '@mui/icons-material/Check';
import SouthEastIcon from '@mui/icons-material/SouthEast';
import "../../Styles/Sidebar.css";
import "../../Styles/Header.css";
import Sidebar from "../SidePannel/Sidebar";
import { useTheme } from "@mui/material/styles";
import Logo from "../../Images/pari_logo_6.png";
import { GetNotification, ChangeNotificationStatus, ChangeAllNotificationStatus } from '../../redux/Actions/userStatusService'
import { useDispatch } from "react-redux";
import { secureTokenManager } from "../../utils/secureTokenManager";
import { io } from "socket.io-client";
import { Base_URL } from "../../Common/Path";
import { useNavigate } from 'react-router-dom';
import {
  AppBar,
  Drawer,
  TextTypography,
  StatusText,
  UserIcons,
} from "./AppbarCommon";

// Security utility functions
const isValidToken = (token) => {
  if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
    return false;
  }
  
  try {
    // Basic JWT structure validation (header.payload.signature)
    const parts = token.split('.');
    if (parts.length !== 3) {
      return false;
    }
    
    // Check if token is expired (basic check)
    const payload = JSON.parse(atob(parts[1]));
    const currentTime = Math.floor(Date.now() / 1000);
    
    if (payload.exp && payload.exp < currentTime) {
      console.warn('Token has expired');
      return false;
    }
    
    return true;
  } catch (error) {
    console.error('Invalid token format:', error);
    return false;
  }
};

const clearSessionAndRedirect = () => {
  localStorage.clear();
  sessionStorage.clear();
  window.location.href = '/login';
};

function Appbar(props) {
  const theme = useTheme();
  const logoSrc = Logo;
  const [colors, setcolors] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = React.useState(true);
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const socketRef = useRef(null);
  const entityIdCheckIntervalRef = useRef(null);
  const hasInitializedRef = useRef(false);
  
  // Get user info from secure storage
  const userInfo = secureTokenManager.getUserInfo();
  const role = userInfo?.role || localStorage.getItem("type"); // Fallback for migration
  const token = secureTokenManager.getAccessToken();

  // Function to fetch notifications
  const fetchNotifications = async () => {
    try {
      const response = await dispatch(GetNotification({
        role: role
      }));
      
      if (response?.payload?.data) {
        const sortedNotifications = sortNotifications(response.payload.data);
        setNotifications(sortedNotifications);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    }
  };

  // Check for entityId and fetch notifications
  useEffect(() => {

      fetchNotifications();
    // If entityId is not available or invalid, set up an interval to check for it
    entityIdCheckIntervalRef.current = setInterval(() => {
        fetchNotifications();
        hasInitializedRef.current = true; // Mark as initialized
        clearInterval(entityIdCheckIntervalRef.current);
    }, 1000); // Check every second
    
    return () => {
      if (entityIdCheckIntervalRef.current) {
        clearInterval(entityIdCheckIntervalRef.current);
      }
    };
  }, [ role, dispatch]);

  // Socket connection - also needs to check for entityId
  useEffect(() => {
  
    const setupSocket = () => {
      // Validate token before setting up socket connection
      if (!isValidToken(token)) {
        console.error('No valid token available for socket connection');
        clearSessionAndRedirect();
        return;
      }

      if (!socketRef.current) {
        // SECURITY FIX: Use 'auth' instead of 'query' to prevent JWT token leakage in logs
        // The 'auth' object is sent during the handshake but not logged in query strings
        socketRef.current = io(Base_URL, {
          auth: { 
            token,
            role: role,
          },
          transports: ['websocket'],
          reconnection: true,
          reconnectionAttempts: 50,
          timeout: 20000,
          forceNew: true // Ensure fresh connection for security
        });

        socketRef.current.on('connect', () => {
          console.log("Connected");
        });

        socketRef.current.on('messageReceived', (notifications) => {
          const notificationArray = Array.isArray(notifications) ? notifications : [notifications];
          
          if (Notification.permission === 'granted') {
            notificationArray.forEach(notification => {
              new Notification(notification.title || 'New Notification', {
                body: notification.message || notification.body,
                icon: '/favicon/favicon-32x32.png'
              });
            });
          }

          setNotifications(prev => {
            const newNotifications = [...notificationArray, ...prev];
            return sortNotifications(newNotifications);
          });
        });

        socketRef.current.on('disconnect', () => {
          console.log("Disconnected");
        });

        socketRef.current.on('connect_error', (error) => {
          console.error("Socket connection error:", error);
          
          // Handle authentication errors
          if (error.message && (
            error.message.includes('Authentication') || 
            error.message.includes('Unauthorized') ||
            error.message.includes('Invalid token')
          )) {
            console.error('Socket authentication failed - clearing session');
            clearSessionAndRedirect();
          }
        });

        socketRef.current.on('error', (error) => {
          console.error("Socket error:", error);
          
          // Handle authentication/authorization errors
          if (error.type === 'UnauthorizedError' || 
              error.code === 'AUTHENTICATION_ERROR' ||
              error.message?.includes('JWT') ||
              error.message?.includes('token')) {
            console.error('Socket unauthorized - clearing session');
            clearSessionAndRedirect();
          }
        });
      }
    };
    
    // First try to get entityId directly
    let entityId = localStorage.getItem("entId");
    
    // Check if entityId is valid
    if (entityId && entityId !== "undefined" && entityId !== "null") {
      setupSocket(entityId);
      return;
    }
    
    // If entityId is not available or invalid, set up an interval to check for it
    const socketCheckInterval = setInterval(() => {
        setupSocket(entityId);
        clearInterval(socketCheckInterval);
    }, 1000); // Check every second
    
    return () => {
      clearInterval(socketCheckInterval);
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, [token]);

  // Sort notifications function - unread first, then by date
  const sortNotifications = (notifs) => {
    if (!notifs) return [];
    
    return [...notifs].sort((a, b) => {
      if (!a.isRead && b.isRead) return -1;
      if (a.isRead && !b.isRead) return 1;
      
      return new Date(b.createdAt) - new Date(a.createdAt);
    });
  };

  const toggleDrawer = () => {
    setOpen(!open);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const unreadCount = notifications?.filter(notification => !notification?.isRead)?.length;

  const handleNotificationClick = async (notification) => {
    try {
      // Only mark as read if it's not already read
      if (!notification.isRead) {
        const response = await dispatch(ChangeNotificationStatus({ 
          id: notification._id, 
          body: { isRead: true } 
        }));

        if (response?.payload?.statusCode === 200) {
          // Update the notification in the state
          setNotifications(prevNotifications => {
            const updatedNotifications = prevNotifications.map(item => 
              item._id === notification._id ? { ...item, isRead: true } : item
            );
            return sortNotifications(updatedNotifications);
          });
        }
      }
    } catch (error) {
      console.error("Error changing notification status:", error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await dispatch(ChangeAllNotificationStatus());
      if (response?.payload?.statusCode === 200) {
        // Update all notifications to read in the state
        setNotifications(prevNotifications => {
          const updatedNotifications = prevNotifications.map(item => ({ ...item, isRead: true }));
          return sortNotifications(updatedNotifications);
        });
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const formatTimeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHrs = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHrs / 24);

    if (diffMins < 60) {
      return `${diffMins} mins ago`;
    } else if (diffHrs < 24) {
      return `${diffHrs} hr ago`;
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else {
      return `${diffDays} days ago`;
    }
  };

  const handleSeeAllClick = () => {
    handleClose(); // Close the notification menu
    navigate('/allnotification'); // Navigate without page reload
  };

  return (
    <>
      <AppBar position="fixed" open={open}>
        <Toolbar
          sx={{
            pr: "24px",
          }}
        >
          <IconButton
            edge="start"
            color="black"
            aria-label="open drawer"
            onClick={toggleDrawer}
            sx={{
              marginRight: "36px",
              color:'black',
              ...(open && { display: "none" }),
            }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            color="inherit"
            noWrap
            sx={{ flexGrow: 1 }}
          >
            {props.title}
            <p style={{ fontSize: "12px", color: "#000" }}>
              <b style={{ color: "gray" }}>{props.mainpage}</b>-{props.subpage}
            </p>
          </Typography>

          <TextTypography />
          <StatusText colors={colors} status={status} />
          
          <IconButton onClick={handleClick}>
            <Badge badgeContent={unreadCount} color="error">
              <NotificationsIcon style={{ color: "#757575" }} />
            </Badge>
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              style: {
                width: '350px',
                maxHeight: '520px',
                borderRadius: '8px',
                padding: 0,
                overflow: 'hidden'
              },
            }}
          >
            <div style={{ 
              padding: '15px 20px', 
              borderBottom: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="h6" style={{ fontWeight: 'bold', fontSize: '16px' }}>
                Notifications
              </Typography>
              {unreadCount > 0 && (
                <Tooltip title="Mark all as read">
                  <IconButton size="small" onClick={handleMarkAllAsRead}>
                    <DoneAllIcon fontSize="small" color="primary" />
                  </IconButton>
                </Tooltip>
              )}
            </div>
            
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {notifications?.length === 0 ? (
                <div style={{ padding: '15px 20px', textAlign: 'center' }}>
                  No Notification Found
                </div>
              ) : (
                notifications?.map((notification) => (
                  <div 
                    key={notification._id} 
                    onClick={() => handleNotificationClick(notification)}
                    style={{ 
                      padding: '16px 20px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'flex-start',
                      borderBottom: '1px solid #f0f0f0',
                      backgroundColor: notification.isRead ? "transparent" : "#f5f5f5"
                    }}
                  >
                    <div 
                      style={{ 
                        width: '36px', 
                        height: '36px', 
                        borderRadius: '50%', 
                        backgroundColor: '#2196f3', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginRight: '12px',
                        color: 'white',
                        fontWeight: 'bold',
                        fontSize: '16px'
                      }}
                    >
                      {notification.isRead ? (
                        <CheckIcon fontSize="small" />
                      ) : (
                        <SouthEastIcon fontSize="small" />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography 
                          style={{ 
                            fontWeight: 'bold', 
                            fontSize: '14px',
                            color: '#333',
                            lineHeight: 1.4
                          }}
                        >
                          {notification.title}
                        </Typography>
                        <Typography 
                          style={{ 
                            fontSize: '14px', 
                            color: '#666',
                            lineHeight: 1.4
                          }}
                        >
                          {notification.message}
                        </Typography>
                      </div>
                    </div>
                    <div>
                      <Typography 
                        style={{ 
                          fontSize: '12px', 
                          color: '#888',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {formatTimeAgo(notification.createdAt)}
                      </Typography>
                    </div>
                  </div>
                ))
              )}
            </div>
          </Menu>
          
          <UserIcons />
        </Toolbar>
      </AppBar>
      <Drawer variant="permanent" open={open}>
        <Toolbar
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: [2],
            py:[1],
            minHeight: "64px !important",
            backgroundColor: "white",
            color: "black",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", justifyContent:"center", gap: 1 }}>
            <Box
              sx={{
                width: '187px',
                height: "87px",
                backgroundColor: "white",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-start",
                padding: "4px",
              }}
            >
              <img 
                src={logoSrc}
                alt="pari" 
                style={{
                  height: "100%",
                  width: "auto",
                  objectFit: "contain"
                }}
              />
            </Box>
           
          </Box>
          {open && (
            <IconButton 
              onClick={toggleDrawer}
              sx={{ 
                display: { xs: 'inline-flex', md: 'none' },
                color: "white",
                "&:hover": {
                  backgroundColor: "rgba(255,255,255,0.1)"
                }
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
        </Toolbar>
        <Sidebar open={open} setOpen={setOpen} />
      </Drawer>
    </>
  );
}

export default Appbar;
